import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { motion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Bell, User, Briefcase, BookOpen, Activity, Award, Palette, Leaf, Globe } from 'lucide-react';

export default function CareerDashboard() {
  const [progress, setProgress] = useState(50);
  const [quote, setQuote] = useState("");
  const [xp, setXp] = useState(120);

  const quotes = [
    "Success is not the key to happiness. Happiness is the key to success.",
    "Opportunities don't happen. You create them.",
    "Your career is your business. It's time to start treating it like one."
  ];

  useEffect(() => {
    setQuote(quotes[Math.floor(Math.random() * quotes.length)]);
  }, []);

  return (
    <div className="p-6 space-y-6 bg-gradient-to-br from-purple-700 via-blue-500 to-indigo-600 min-h-screen text-white">
      {/* Character Profile Section */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white p-6 rounded-lg shadow-lg flex items-center space-x-6 transform hover:scale-105 transition-transform"
      >
        <div className="w-24 h-24 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center text-white text-4xl shadow-lg border-4 border-white">
          🐢
        </div>
        <div className="text-gray-900">
          <h3 className="text-2xl font-bold">The Awkward Turtle</h3>
          <p className="text-gray-600">Strategic Thinker | Problem Solver | Adaptive Learner</p>
          <Badge className="mt-2 bg-green-500 text-white">XP: {xp}</Badge>
        </div>
      </motion.div>

      {/* Career Path Selector */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        className="bg-white p-6 rounded-lg shadow-lg"
      >
        <h3 className="text-xl font-semibold text-gray-900">Career Paths</h3>
        <div className="flex flex-col space-y-4 py-4">
          <div className="flex items-center space-x-2 bg-blue-100 p-3 rounded-lg shadow-sm">
            <Briefcase size={20} className="text-blue-500" />
            <span className="text-gray-900 font-medium">Software Developer - Ideal for logical problem-solvers who enjoy coding and innovation.</span>
          </div>
          <div className="flex items-center space-x-2 bg-green-100 p-3 rounded-lg shadow-sm">
            <Leaf size={20} className="text-green-500" />
            <span className="text-gray-900 font-medium">Sustainability Consultant - Great for those passionate about environmental impact.</span>
          </div>
          <div className="flex items-center space-x-2 bg-red-100 p-3 rounded-lg shadow-sm">
            <Palette size={20} className="text-red-500" />
            <span className="text-gray-900 font-medium">Graphic Designer - Ideal for artistic minds who love visual storytelling.</span>
          </div>
          <div className="flex items-center space-x-2 bg-yellow-100 p-3 rounded-lg shadow-sm">
            <Globe size={20} className="text-yellow-500" />
            <span className="text-gray-900 font-medium">Travel Blogger - Perfect for adventurers who enjoy writing and sharing experiences.</span>
          </div>
        </div>
      </motion.div>

      {/* Personalized Job Board */}
      <div className="bg-white p-6 rounded-lg shadow-lg">
        <h3 className="text-xl font-semibold text-gray-900">Recommended Jobs</h3>
        <ul className="mt-4 space-y-3">
          <li className="border p-3 rounded-md bg-blue-200 hover:bg-blue-300 text-gray-900">Frontend Developer - TechCorp</li>
          <li className="border p-3 rounded-md bg-green-200 hover:bg-green-300 text-gray-900">Data Scientist - InnovateX</li>
          <li className="border p-3 rounded-md bg-red-200 hover:bg-red-300 text-gray-900">Product Manager - GrowthHub</li>
        </ul>
      </div>

      {/* Progress Tracker */}
      <div className="bg-gray-900 p-6 rounded-lg shadow-lg text-center">
        <h3 className="text-xl font-semibold">Career Progress</h3>
        <Progress value={progress} className="mt-4 bg-gray-700 text-white" />
        <span className="text-sm text-gray-400">{progress}% Complete</span>
      </div>

      {/* XP and Achievements */}
      <div className="bg-blue-700 p-6 rounded-lg flex justify-between items-center shadow-lg">
        <Award size={28} className="text-yellow-400" />
        <span className="text-lg">Earn XP by completing career steps!</span>
        <Button variant="secondary" className="bg-white text-blue-700">View Achievements</Button>
      </div>
    </div>
  );
}